import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';
import { AlertSingleLine, DataCard } from '@sdir/sds';
import { ApplicationLayout } from '../Organisms/ApplicationLayout';
import ApplicationSteps from '../Molecules/ApplicationSteps';
import PaymentWindowModule from '../Organisms/PaymentWindowModule';
import { useCurrentApplication } from '../../services/hooks/useCurrentApplication';
import { routes } from '../Routes';
import { ApplicationStatus } from '../../constants/enums';

const PaymentPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { application: currentApplication } = useCurrentApplication();
  const [chargeIds, setChargeIds] = useState<number[]>(currentApplication?.result?.chargeIds || []);
  const hasChargeIds = chargeIds && chargeIds.length > 0;

  useEffect(() => {
    if (currentApplication?.result?.chargeIds) {
      setChargeIds(currentApplication?.result?.chargeIds || []);
    }
  }, [currentApplication?.result]);

  const { transactionId, responseCode } = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return {
      transactionId: search.get('transactionId') || '',
      responseCode: search.get('responseCode') || '',
    };
  }, [location?.search]);

  const goToApplications = () => {
    navigate(routes.home);
  };

  const showPaymentWindow = hasChargeIds && !currentApplication.loading;
  const showNoChargesMsg = !hasChargeIds && !currentApplication?.loading;

  return (
    <ApplicationLayout
      canNavBack={currentApplication?.result?.applicationStatus === ApplicationStatus.Draft}
      heading={intl.formatMessage({ id: 'application.new.title' })}
      ingress={intl.formatMessage({ id: 'application.new.ingress' })}
      exitLabel={intl.formatMessage({ id: 'general.backtoapplications' })}
      navBackLabel={intl.formatMessage({ id: 'general.back' })}
      timeline={<ApplicationSteps disableNavigation />}
    >
      <DataCard>
        <PaymentInfo>
          <FormContent>
            <h2>{intl.formatMessage({ id: 'application.payment.title' })}</h2>
            {showPaymentWindow && (
              <PaymentWindowModule
                errorCallback={() => {}}
                cancelCallback={goToApplications}
                successCallback={goToApplications}
                chargeIds={chargeIds}
                transactionId={transactionId}
                responseCode={responseCode}
              />
            )}
            {showNoChargesMsg && (
              <AlertSingleLine
                title={intl.formatMessage({ id: 'application.payment.title' })}
                text={intl.formatMessage({ id: 'application.payment.couldnotstart' })}
                type="error"
                hideClose
              />
            )}
          </FormContent>
        </PaymentInfo>
      </DataCard>
    </ApplicationLayout>
  );
};

export default PaymentPage;

const PaymentInfo = styled.div`
  padding: 10px 35px 40px 35px;
  min-height: 400px;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ErrorWrapper = styled.div`
  margin-bottom: 20px;
`;
